import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import MaterialTable from 'material-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// components
import Modal from '../../../../components/modal/Modal';
import RainfallChart from '../../chart/RainfallChart';

// style
import styles from '../style';
import tableIcons from '../../../../components/table/MaterialTableIcon';
import { StyledIconButton } from '../../../../components/button/Button';
import RainfallStatusColor from '../../map/RinfallStatusColor';
import exportCsv from '../../../../utils/export/Csv';

const useStyles = makeStyles(styles);

export default function RainTodayList(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [type] = useState('rainfallToday');
  const {
    datas, handleSearchMarker, currentMode, currentArea, userIsLogin,
  } = props;
  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟ'),
    subheader: null,
    content: null,
  });
  const isExport = (userIsLogin) ? { csv: true } : userIsLogin;

  function filterData(currentData, mode, area) {
    let filterD = [];

    if (mode === 'province' && area.code !== '0') {
      filterD = datas.filter((d) => d.geocode.province_code === area.code);
    } else if (currentMode === 'basin' && area.code !== '0') {
      filterD = datas.filter((d) => d.basin.basin_code === area.code * 1);
    } else {
      filterD = currentData;
    }

    return filterD;
  }

  function formatNumber(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const handleChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: `${t('กราฟฝนสะสม 3 วัน')} ${data.station.tele_station_name.th}`,
      subheader: null,
      content: <RainfallChart
        filterData={datas}
        id={data.station.id}
        provinceId={data.geocode.province_code}
        selectedType={type}
        year={(data.rainfall_datetime).substring(0, 4)}
        month={(data.rainfall_datetime).substring(5, 7)}
        classes={classes}
      />,
    });
  };

  // force modal close
  useEffect(() => {
    if (modalData.open) modalData.open = false;
  }, [modalData.open]);

  return (
    <>
      <Paper className={classes.root}>
        <MaterialTable
          icons={tableIcons}
          columns={[
            {
              title: t('ชื่อสถานี'),
              title_export: t('ชื่อสถานี'),
              field: 'station.tele_station_name.th',
              width: 250,
              headerStyle: {
                left: 0,
                position: 'sticky',
                zIndex: 50,
              },
              cellStyle: {
                left: 0,
                position: 'sticky',
                wordWrap: 'normal',
                padding: '0.3em',
              },
              render: (rowData) => {
                if (rowData.station.tele_station_lat && rowData.station.tele_station_long) {
                  return (
                    <Box display="flex" style={{ backgroundColor: 'white' }}>
                      <Button
                        onClick={handleSearchMarker(
                          rowData.station.id,
                          rowData.station.tele_station_lat,
                          rowData.station.tele_station_long,
                        )}
                        title={t('แสดงตำแหน่งบนแผนที่')}
                      >
                        {`${rowData.station.tele_station_name.th}`}
                      </Button>
                    </Box>
                  );
                }

                return (
                  <Button
                    onClick={handleSearchMarker(
                      rowData.station.id,
                      rowData.station.tele_station_lat,
                      rowData.station.tele_station_long,
                    )}
                    title={t('แสดงตำแหน่งบนแผนที่')}
                  >
                    {`สถานี${rowData.station.tele_station_name.th}`}
                  </Button>
                );
              },
            },
            {
              title: t('รหัสสถานี'),
              type: 'string',
              field: 'station.tele_station_oldcode',
              hidden: true,
              export: false,
              searchable: true,
            },
            {
              title: t('ตำบล'),
              title_export: t('ตำบล'),
              field: 'geocode.tumbon_name.th',
              type: 'string',
              hidden: true,
            },
            {
              title: t('อำเภอ'),
              title_export: t('อำเภอ'),
              field: 'geocode.amphoe_name.th',
              type: 'string',
              hidden: true,
            },
            {
              title: t('ที่ตั้ง'),
              title_export: t('จังหวัด'),
              field: 'geocode.province_name.th',
              type: 'string',
              width: 350,
              render: (rowData) => `${t('ต.')}${rowData.geocode.tumbon_name.th} ${t('อ.')}${rowData.geocode.amphoe_name.th} ${t('จ.')}${rowData.geocode.province_name.th}`,
            },
            {
              title: t('ลุ่มน้ำ'),
              title_export: t('ลุ่มน้ำ'),
              field: 'basin.basin_name.th',
              type: 'string',
              width: 150,
              hidden: !userIsLogin,
              render: (rowData) => rowData.basin.basin_name.th,
            },
            {
              title: t('เวลา'),
              title_export: t('วันที่'),
              field: 'rainfall_datetime',
              type: 'date',
              width: 150,
              render: (rowData) => `${rowData.rainfall_datetime.substring(rowData.rainfall_datetime.length - 5)} น.`,
            },
            {
              title: t('ฝนสะสม (มม.)'),
              title_export: t('ฝนสะสม (มม.)'),
              field: 'rainfall_value',
              type: 'numeric',
              width: 115,
              render: (rowData) => {
                const rainfall = parseFloat(rowData.rainfall_value).toFixed(2);
                const rainfallStatusC = RainfallStatusColor(rainfall, type).color.statusColor;
                const rainfallStatusFontColor = RainfallStatusColor(rainfall, type)
                  .color.constrastColor;

                return (
                  <Box
                    borderRadius={3}
                    style={{
                      align: 'center',
                      textAlign: 'center',
                      margin: '0 auto',
                      backgroundColor: rainfallStatusC,
                      color: rainfallStatusFontColor,
                      width: '45px',
                    }}
                  >
                    {formatNumber(rowData.rainfall_value)}
                  </Box>
                );
              },
            },
            {
              title: t('หน่วยงาน'),
              title_export: t('หน่วยงาน'),
              field: 'agency.agency_name.th',
              type: 'string',
              hidden: true,
              export: true,
            },
            {
              title: t('รหัสสถานี'),
              title_export: t('รหัสสถานี'),
              field: 'station.tele_station_oldcode',
              type: 'string',
              hidden: true,
              export: true,
            },
            {
              title_export: 'Latitude',
              type: 'string',
              field: 'station.tele_station_lat',
              hidden: true,
              export: true,
            },
            {
              title_export: 'Longitude',
              type: 'string',
              field: 'station.tele_station_long',
              hidden: true,
              export: true,
            },
            {
              field: 'graph',
              type: 'numeric',
              sorting: false,
              width: 115,
              render: (rowData) => (
                <>
                  <Tooltip title={t('กราฟฝน')}>
                    <StyledIconButton onClick={handleChart(rowData)}>
                      <FontAwesomeIcon icon={faChartLine} fontSize="small" style={{ padding: 0, color: '#888' }} />
                    </StyledIconButton>
                  </Tooltip>
                </>
              ),
            },
          ]}
          data={filterData(datas, currentMode, currentArea)}
          localization={{
            toolbar: {
              searchPlaceholder: t('ค้นหา'),
              exportTitle: t('ดาวน์โหลดข้อมูล ฝนวันนี้'),
            },
            pagination: {
              labelRowsSelect: t('แถว'),
            },
            body: { emptyDataSourceMessage: datas.length === 0 ? <h3>{t('กำลังโหลดข้อมูล...')}</h3> : <h3>{t('ไม่มีข้อมูลตามเงื่อนไข')}</h3> },
          }}
          options={{
            cellStyle: {
              padding: 8,
            },
            pageSize: 10,
            sorting: true,
            searchFieldStyle: {
              width: '50%',
              alignSelf: 'flex-end',
            },
            rowStyle: {
              wordWrap: 'break-word',
            },
            toolbar: true,
            showTitle: false,
            search: true,
            exportButton: isExport,
            exportCsv,
          }}
        />
        <Modal
          open={modalData.open}
          modalHeader={modalData.header}
          modalSubHeader={modalData.subheader}
          modalContent={modalData.content}
        />
      </Paper>
    </>
  );
}

RainTodayList.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  handleSearchMarker: PropTypes.func.isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
  userIsLogin: PropTypes.bool.isRequired,
};
